import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { Order, OrderStatus, ItemStatus } from '../lib/types';
import { ORDER_STATUSES, STATUS_LABELS, STATUS_COLORS, ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '../lib/types';
import * as api from '../lib/api';

const ITEM_STATUSES: ItemStatus[] = [
  'AWAITING_DETAILS',
  'ORIGIN_DRAFTING',
  'ORIGIN_APPROVED',
  'POEM_DRAFTING',
  'POEM_APPROVED',
  'FILES_READY_FOR_EDIT',
  'FILES_READY',
  'ENGRAVED',
  'CLEANED',
  'PRODUCED',
  'PACKAGED',
];

interface Props {
  id: string;
}

// ---------------------------------------------------------------------------
// Shared styling. Buttons are deliberately small: this is a dense admin tool
// used many times a day, not a marketing page.
// ---------------------------------------------------------------------------
const BTN_PRIMARY =
  'inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SECONDARY =
  'inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-medium px-3 py-1.5 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_ICON =
  'inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed';
const LINK = 'text-blue-600 hover:underline text-sm';

type IconName =
  | 'pencil' | 'check' | 'x' | 'refresh' | 'clipboard' | 'download'
  | 'trash' | 'chevron-right' | 'cloud-down' | 'plus' | 'arrow-left';

const ICON_PATHS: Record<IconName, string> = {
  'pencil': 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125',
  'check': 'M4.5 12.75l6 6 9-13.5',
  'x': 'M6 18L18 6M6 6l12 12',
  'refresh': 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182',
  'clipboard': 'M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184',
  'download': 'M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3',
  'trash': 'M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0',
  'chevron-right': 'M8.25 4.5l7.5 7.5-7.5 7.5',
  'cloud-down': 'M12 9.75v6.75m0 0l-3-3m3 3l3-3m-8.25 6a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z',
  'plus': 'M12 4.5v15m7.5-7.5h-15',
  'arrow-left': 'M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18',
};

function Icon({ name, class: cls = 'w-4 h-4' }: { name: IconName; class?: string }) {
  return (
    <svg
      class={cls}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

/** Inline status line under an action row: neutral for info, red for errors. */
function Notice({ text, tone = 'info' }: { text: string | null; tone?: 'info' | 'error' }) {
  if (!text) return null;
  return (
    <p class={`mt-3 text-sm ${tone === 'error' ? 'text-red-600' : 'text-gray-500'}`} role={tone === 'error' ? 'alert' : 'status'}>
      {text}
    </p>
  );
}

function sourceBadge(order: Order): { label: string; cls: string } {
  if (order.amazonOrderId && AMAZON_ORDER_ID.test(order.amazonOrderId)) {
    return { label: 'Amazon', cls: 'bg-orange-50 text-orange-700 ring-orange-200' };
  }
  if (order.wooOrderId || order.source === 'woocommerce' || order.source === 'web') {
    return { label: 'Website', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' };
  }
  return { label: 'Manual', cls: 'bg-gray-50 text-gray-600 ring-gray-200' };
}

// ---------------------------------------------------------------------------

export function OrderDetail({ id }: Props) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrder();
  }, [id]);

  async function loadOrder() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getOrder(id);
      setOrder(data.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div class="text-center py-8 text-gray-500">Loading order...</div>;
  }

  if (error || !order) {
    return (
      <div class="text-center py-8">
        <div class="text-red-600 mb-4">{error || 'Order not found'}</div>
        <button onClick={() => route('/')} class={LINK}>
          Back to orders
        </button>
      </div>
    );
  }

  return (
    <div>
      <OrderHeader order={order} onUpdate={setOrder} />
      <OrderDetailsCard order={order} onUpdate={setOrder} />
      <OrderProofSection order={order} onUpdate={setOrder} />
      <ItemsListSection order={order} onUpdate={setOrder} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header: who the order is for, where it came from, and its status.
// ---------------------------------------------------------------------------
function OrderHeader({ order, onUpdate }: { order: Order; onUpdate: (order: Order) => void }) {
  const [savingStatus, setSavingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const names = order.items.map(i => i.name?.trim()).filter(Boolean);
  const title = names.length > 0 ? names.join(', ') : 'Order';
  const badge = sourceBadge(order);

  async function handleStatusChange(newStatus: OrderStatus) {
    setSavingStatus(true);
    setError(null);
    try {
      const result = await api.updateOrder(order.orderId, { status: newStatus });
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  }

  return (
    <div class="mb-6">
      <button onClick={() => route('/')} class="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-3">
        <Icon name="arrow-left" class="w-3.5 h-3.5" />
        Orders
      </button>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="text-2xl font-semibold text-gray-900 truncate">{title}</h1>
          <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
            <span class={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${badge.cls}`}>
              {badge.label}
            </span>
            <span class="font-mono text-xs">{order.orderId}</span>
            <span>Created {new Date(order.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          {savingStatus && <span class="text-xs text-gray-400">Saving…</span>}
          <select
            value={order.status}
            onChange={(e) => handleStatusChange((e.target as HTMLSelectElement).value as OrderStatus)}
            disabled={savingStatus}
            title="Order status"
            class={`px-3 py-1 text-sm rounded-full border-0 cursor-pointer ${STATUS_COLORS[order.status]} ${savingStatus ? 'opacity-50' : ''}`}
          >
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Notice text={error} tone="error" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details: buyer name (inline edit), Amazon order link, Amazon pull.
// ---------------------------------------------------------------------------
function OrderDetailsCard({ order, onUpdate }: { order: Order; onUpdate: (order: Order) => void }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buyerName, setBuyerName] = useState(order.buyerName);
  const [pulling, setPulling] = useState(false);
  const [notice, setNotice] = useState<{ text: string; tone: 'info' | 'error' } | null>(null);

  useEffect(() => {
    setBuyerName(order.buyerName);
  }, [order.buyerName]);

  const isAmazonOrder = !!order.amazonOrderId && AMAZON_ORDER_ID.test(order.amazonOrderId);

  async function handleSave() {
    if (buyerName === order.buyerName) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const result = await api.updateOrder(order.orderId, { buyerName });
      onUpdate(result.order);
      setEditing(false);
    } catch (err) {
      setNotice({ text: err instanceof Error ? err.message : 'Failed to save', tone: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setEditing(false);
    setBuyerName(order.buyerName);
  }

  async function handlePullFromAmazon() {
    setPulling(true);
    setNotice(null);
    try {
      const result = await api.pullAmazonCustomization(order.orderId);
      onUpdate(result.order);
      const { order: orderFields = [], ...itemFills } = result.filled;
      const itemFieldCount = Object.values(itemFills).reduce((n, fields) => n + fields.length, 0);
      const itemCount = Object.keys(itemFills).length;
      const buyerNameNote = orderFields.includes('buyerName') ? ' Buyer name filled.' : '';
      let text: string;
      if (result.itemsWithData === 0 && itemFieldCount === 0) {
        text = `Amazon returned no customization data for this order.${buyerNameNote}`;
      } else if (itemFieldCount === 0) {
        text = `Nothing to fill: every item field already had a value.${buyerNameNote}`;
      } else {
        text = `Filled ${itemFieldCount} field${itemFieldCount === 1 ? '' : 's'} across ${itemCount} item${itemCount === 1 ? '' : 's'}.${buyerNameNote}`;
      }
      setNotice({ text, tone: 'info' });
    } catch (err) {
      setNotice({ text: err instanceof Error ? err.message : 'Pull from Amazon failed', tone: 'error' });
    } finally {
      setPulling(false);
    }
  }

  return (
    <div class="bg-white rounded-lg shadow p-5 mb-4">
      <dl class="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 text-sm">
        <div>
          <dt class="text-gray-500">Buyer name</dt>
          <dd class="mt-0.5">
            {editing ? (
              <form
                class="flex items-center gap-1"
                onSubmit={(e) => { e.preventDefault(); handleSave(); }}
              >
                <input
                  type="text"
                  value={buyerName}
                  onInput={(e) => setBuyerName((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit(); }}
                  class="border border-gray-300 rounded-md px-2 py-1 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  disabled={saving}
                  aria-label="Buyer name"
                />
                <button type="submit" class={BTN_ICON} disabled={saving} title="Save (Enter)">
                  <Icon name="check" />
                </button>
                <button type="button" class={BTN_ICON} onClick={cancelEdit} disabled={saving} title="Cancel (Esc)">
                  <Icon name="x" />
                </button>
              </form>
            ) : (
              <span class="inline-flex items-center gap-1">
                <span class={`font-medium ${order.buyerName ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                  {order.buyerName || 'Not set'}
                </span>
                <button class={BTN_ICON} onClick={() => setEditing(true)} title="Edit buyer name">
                  <Icon name="pencil" class="w-3.5 h-3.5" />
                </button>
              </span>
            )}
          </dd>
        </div>

        {order.amazonOrderId && (
          <div>
            <dt class="text-gray-500">Amazon order</dt>
            <dd class="mt-1 font-mono text-xs leading-6">
              <AmazonOrderLink orderId={order.amazonOrderId} />
            </dd>
          </div>
        )}

        {order.wooOrderId && (
          <div>
            <dt class="text-gray-500">Website order</dt>
            <dd class="mt-1 font-mono text-xs leading-6">#{order.wooOrderId}</dd>
          </div>
        )}

        {isAmazonOrder && (
          <div class="sm:text-right">
            <dt class="sr-only">Amazon actions</dt>
            <dd>
              <button
                onClick={handlePullFromAmazon}
                disabled={pulling}
                class={BTN_SECONDARY}
                title="Fetch the buyer's customization (name, pronouns, material, notes) and buyer name from Amazon. Fills blank fields only."
              >
                <Icon name="cloud-down" />
                {pulling ? 'Pulling…' : 'Pull from Amazon'}
              </button>
            </dd>
          </div>
        )}
      </dl>
      <Notice text={notice?.text ?? null} tone={notice?.tone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Proof: one compact row. Download is the only text button; regenerate and
// copy-message are icon buttons with tooltips.
// ---------------------------------------------------------------------------
function OrderProofSection({ order, onUpdate }: { order: Order; onUpdate: (order: Order) => void }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle');

  const readyItems = order.items.filter(
    item => item.poemApproved && (item.status === 'POEM_APPROVED' || item.status === 'FILES_READY')
  );

  if (readyItems.length === 0) {
    return null;
  }

  async function handleGenerateProof() {
    setGenerating(true);
    setError(null);
    try {
      const result = await api.generateOrderProof(order.orderId);
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Proof generation failed');
    } finally {
      setGenerating(false);
    }
  }

  async function handleCopyMessage() {
    setCopyState('copying');
    try {
      const { text } = await api.getProofMessage();
      await copyToClipboard(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2500);
    } catch (err) {
      console.error('Copy customer message failed', err);
      setCopyState('failed');
      setTimeout(() => setCopyState('idle'), 4000);
    }
  }

  const proofUrl = order.proofPdfUrl
    ? `https://name-frames.link-a-charm.workers.dev/pdf/${order.proofPdfUrl}`
    : null;

  const readiness =
    readyItems.length === order.items.length
      ? 'All items ready'
      : `${readyItems.length} of ${order.items.length} items ready`;

  const copyLabel =
    copyState === 'copied' ? 'Copied' :
    copyState === 'failed' ? 'Copy failed' :
    copyState === 'copying' ? 'Copying…' : null;

  return (
    <div class="bg-white rounded-lg shadow p-5 mb-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h2 class="text-base font-semibold text-gray-900">Proof PDF</h2>
          <p class="text-sm text-gray-500">{readiness}</p>
        </div>

        {proofUrl ? (
          <div class="flex items-center gap-1">
            <a href={proofUrl} download class={BTN_PRIMARY} title="Download the proof PDF">
              <Icon name="download" />
              Download
            </a>
            <button
              onClick={handleGenerateProof}
              disabled={generating}
              class={`${BTN_ICON} ml-1`}
              title="Regenerate the proof PDF"
              aria-label="Regenerate the proof PDF"
            >
              <Icon name="refresh" class={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCopyMessage}
              disabled={copyState === 'copying'}
              class={`${BTN_ICON} ${copyState === 'copied' ? 'text-green-600' : ''}`}
              title="Copy the customer message for Buyer-Seller Messaging"
              aria-label="Copy the customer message for Buyer-Seller Messaging"
            >
              <Icon name={copyState === 'copied' ? 'check' : 'clipboard'} />
            </button>
            {copyLabel && (
              <span class={`text-xs ${copyState === 'failed' ? 'text-red-600' : 'text-gray-500'}`} role="status">
                {copyLabel}
              </span>
            )}
          </div>
        ) : (
          <button onClick={handleGenerateProof} disabled={generating} class={BTN_PRIMARY}>
            <Icon name={generating ? 'refresh' : 'download'} class={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating…' : 'Generate proof'}
          </button>
        )}
      </div>
      <Notice text={error} tone="error" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Items: rows open the item workflow; status is editable in place.
// ---------------------------------------------------------------------------
function ItemsListSection({ order, onUpdate }: { order: Order; onUpdate: (order: Order) => void }) {
  const [adding, setAdding] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPronouns, setNewItemPronouns] = useState('they/them');
  const [error, setError] = useState<string | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [updatingStatusIndex, setUpdatingStatusIndex] = useState<number | null>(null);

  async function handleItemStatusChange(index: number, newStatus: ItemStatus) {
    setUpdatingStatusIndex(index);
    setError(null);
    try {
      const result = await api.updateItem(order.orderId, index, { status: newStatus });
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item status');
    } finally {
      setUpdatingStatusIndex(null);
    }
  }

  async function handleAddItem() {
    if (!newItemName.trim()) return;
    setError(null);
    try {
      const result = await api.addItem(order.orderId, {
        name: newItemName,
        pronouns: newItemPronouns,
      });
      onUpdate(result.order);
      setNewItemName('');
      setNewItemPronouns('they/them');
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item');
    }
  }

  async function handleDeleteItem(index: number) {
    if (!confirm(`Delete item "${order.items[index].name || `Item ${index + 1}`}"?`)) {
      return;
    }
    setDeletingIndex(index);
    setError(null);
    try {
      const result = await api.deleteItem(order.orderId, index);
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeletingIndex(null);
    }
  }

  return (
    <div class="bg-white rounded-lg shadow p-5">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-base font-semibold text-gray-900">
          Items <span class="text-gray-400 font-normal">({order.items.length})</span>
        </h2>
        {!adding && (
          <button onClick={() => setAdding(true)} class={BTN_SECONDARY}>
            <Icon name="plus" class="w-3.5 h-3.5" />
            Add item
          </button>
        )}
      </div>

      <div class="border rounded-lg overflow-hidden">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase w-10">#</th>
              <th class="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th class="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th class="px-2 py-2.5 w-20"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            {order.items.map((item, index) => (
              <tr
                key={item.itemId}
                class="group hover:bg-gray-50 cursor-pointer"
                onClick={() => route(`/orders/${order.orderId}/items/${index}`)}
                title="Open item workflow"
              >
                <td class="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-900">
                    {item.name || <span class="text-gray-400 italic">Unnamed</span>}
                  </div>
                  {item.pronouns && (
                    <div class="text-xs text-gray-500">{item.pronouns}</div>
                  )}
                </td>
                <td class="px-4 py-3">
                  <select
                    value={item.status}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleItemStatusChange(index, (e.target as HTMLSelectElement).value as ItemStatus);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={updatingStatusIndex === index}
                    title="Item status"
                    class={`px-2 py-1 text-xs rounded-full border-0 cursor-pointer ${ITEM_STATUS_COLORS[item.status]} ${updatingStatusIndex === index ? 'opacity-50' : ''}`}
                  >
                    {ITEM_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {ITEM_STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </td>
                <td class="px-2 py-3">
                  <div class="flex items-center justify-end gap-0.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(index);
                      }}
                      disabled={deletingIndex === index || order.items.length <= 1}
                      class={`${BTN_ICON} hover:text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-0`}
                      title={order.items.length <= 1 ? "Can't delete the last item" : 'Delete item'}
                      aria-label="Delete item"
                    >
                      <Icon name="trash" />
                    </button>
                    <Icon name="chevron-right" class="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && (
        <form
          class="mt-4 flex flex-wrap items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); handleAddItem(); }}
        >
          <input
            type="text"
            placeholder="Name for the frame"
            value={newItemName}
            onInput={(e) => setNewItemName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setNewItemName(''); } }}
            class="flex-1 min-w-[12rem] border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
            aria-label="Name for the frame"
          />
          <select
            value={newItemPronouns}
            onChange={(e) => setNewItemPronouns((e.target as HTMLSelectElement).value)}
            class="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            aria-label="Pronouns"
          >
            <option value="he/him">he/him</option>
            <option value="she/her">she/her</option>
            <option value="they/them">they/them</option>
          </select>
          <button type="submit" disabled={!newItemName.trim()} class={BTN_PRIMARY}>
            Add
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setNewItemName(''); }}
            class={BTN_SECONDARY}
          >
            Cancel
          </button>
        </form>
      )}

      <Notice text={error} tone="error" />
    </div>
  );
}

// ---------------------------------------------------------------------------

const AMAZON_ORDER_ID = /^\d{3}-\d{7}-\d{7}$/;

/** Amazon order ID linking to the order in Seller Central. Falls back to plain text
 *  for manual orders whose reference isn't an Amazon order ID. */
function AmazonOrderLink({ orderId }: { orderId: string }) {
  if (!AMAZON_ORDER_ID.test(orderId)) return <>{orderId}</>;
  return (
    <a
      href={`https://sellercentral.amazon.com/orders-v3/order/${orderId}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Open in Seller Central"
      class="hover:underline"
    >
      {orderId}
      <svg
        class="inline w-2.5 h-2.5 ml-1 align-super text-gray-400"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M5 2H2.5A.5.5 0 0 0 2 2.5v7a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5V7" />
        <path d="M7 2h3v3M10 2 5.5 6.5" />
      </svg>
      <span class="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

/** Clipboard write with a textarea fallback for browsers without the async API. */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) throw new Error('execCommand copy returned false');
}
